import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { Handshake, ChevronRight, ArrowRight, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

/**
 * Public sponsorship proposal page — mirrors the club's designed
 * proposal PDF (cover → Why sponsor → History → Teams → Gold/Silver
 * /Bronze/Round tiers). Content is served by /api/sponsorship-proposal
 * and edited via /a/sponsors/proposal.
 *
 * Font choices intentionally lean on Barlow Condensed (already loaded by
 * the landing) for the giant italic headings and Nunito/Inter for body,
 * matching the mock the club shared.
 */
export default function SponsorProposal() {
  const [p, setP] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/sponsorship-proposal");
        if (alive) setP(data);
      } catch {
        if (alive) setP({});
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!p) {
    return (
      <PublicLayout>
        <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Loading proposal…</div>
      </PublicLayout>
    );
  }

  const tiers = p.tiers || [];

  return (
    <PublicLayout>
      {/* ============ COVER ============ */}
      <section className="relative bg-[hsl(var(--secondary))]/40" data-testid="proposal-cover">
        {p.cover_image_url && (
          <div className="relative aspect-[3/2] sm:aspect-[16/9] w-full overflow-hidden">
            <img src={p.cover_image_url} alt="Sponsorship cover" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        )}
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-8 sm:py-12 text-center">
          <div
            className="inline-block px-6 py-2 rounded-full text-white text-lg sm:text-xl font-semibold mb-4"
            style={{ background: "#4169E1" }}
          >
            {p.cover_year || "2024/25"}
          </div>
          <h1
            className="text-4xl sm:text-6xl lg:text-7xl uppercase tracking-tight leading-none"
            style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif", fontWeight: 900, color: "#1e3a8a" }}
          >
            {p.cover_title || "Sponsorship Proposal"}
          </h1>
          {p.cover_intro && (
            <p className="mt-6 max-w-2xl mx-auto text-sm sm:text-base text-foreground leading-relaxed">
              {p.cover_intro}
            </p>
          )}
          <Button
            asChild
            className="mt-8 rounded-full bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]/90 h-12 px-8 text-base"
            data-testid="proposal-contact-btn"
          >
            <a href={`mailto:${p.team_email || "derwentcc@gmail.com"}`}>Enquire about sponsorship <ArrowRight className="ml-2 h-4 w-4" /></a>
          </Button>
        </div>
      </section>

      {/* ============ WHY SPONSOR ============ */}
      <section className="border-t border-border" data-testid="proposal-why">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-10 sm:py-14 grid gap-8 lg:grid-cols-[1fr_320px] items-start">
          <div>
            <ProposalHeading kicker="Why" title="Sponsor?" />
            {p.why_intro && <p className="mt-4 text-sm sm:text-base leading-relaxed whitespace-pre-line">{p.why_intro}</p>}
            {p.why_impact_intro && <p className="mt-4 text-sm sm:text-base leading-relaxed">{p.why_impact_intro}</p>}
            {(p.why_bullets || []).length > 0 && (
              <ul className="mt-3 space-y-2 text-sm sm:text-base list-disc pl-5">
                {(p.why_bullets || []).map((b, i) => (
                  <li key={i} className="whitespace-pre-line leading-relaxed">{b}</li>
                ))}
              </ul>
            )}
            {p.why_footnote && <p className="mt-4 text-xs text-muted-foreground italic">{p.why_footnote}</p>}
          </div>
          {p.why_image_url && (
            <div className="rounded-full overflow-hidden aspect-square border-4" style={{ borderColor: "#4169E1" }}>
              <img src={p.why_image_url} alt="Why sponsor" className="w-full h-full object-cover" />
            </div>
          )}
        </div>
      </section>

      {/* ============ HISTORY + TEAMS ============ */}
      <section className="border-t border-border bg-[hsl(var(--secondary))]/40" data-testid="proposal-history">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-10 sm:py-14">
          <ProposalHeading kicker="Our" title="History" />
          {p.history_text && <p className="mt-4 text-sm sm:text-base whitespace-pre-line leading-relaxed">{p.history_text}</p>}
          {(p.teams_intro || (p.teams_image_urls || []).length > 0) && (
            <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_1fr] items-start">
              <div>
                <ProposalHeading kicker="Our" title="Teams" small />
                {p.teams_intro && <p className="mt-3 text-sm sm:text-base whitespace-pre-line leading-relaxed font-semibold">{p.teams_intro}</p>}
              </div>
              {(p.teams_image_urls || []).length > 0 && (
                <div className="grid grid-cols-2 gap-3">
                  {(p.teams_image_urls || []).slice(0, 4).map((u, i) => (
                    <div key={i} className="rounded-full aspect-square overflow-hidden border-4" style={{ borderColor: "#4169E1" }}>
                      <img src={u} alt={`Team ${i + 1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ============ TIERS ============ */}
      <section className="border-t border-border" data-testid="proposal-tiers">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-10 sm:py-14 space-y-12">
          {tiers.map((t, i) => (
            <TierBlock key={t.name || `tier-${i}`} tier={t} flip={i % 2 === 1} />
          ))}
        </div>
      </section>

      {/* ============ MEET THE TEAM ============ */}
      {(p.team_intro || (p.team_members || []).length > 0) && (
        <section className="border-t border-border bg-[hsl(var(--secondary))]/40" data-testid="proposal-team">
          <div className="mx-auto max-w-4xl px-5 sm:px-8 py-10 sm:py-14 grid gap-8 lg:grid-cols-[1fr_1fr] items-start">
            <div>
              <ProposalHeading kicker="Meet The" title="Team" />
              {p.team_intro && (
                <p className="mt-4 text-sm sm:text-base whitespace-pre-line leading-relaxed">{p.team_intro}</p>
              )}
              {p.team_email && (
                <a
                  href={`mailto:${p.team_email}`}
                  className="inline-block mt-4 text-lg sm:text-2xl font-bold underline decoration-2 underline-offset-4"
                  style={{ color: "#1e3a8a" }}
                  data-testid="proposal-team-email"
                >
                  {p.team_email}
                </a>
              )}
            </div>
            {(p.team_members || []).length > 0 && (
              <div className="rounded-3xl p-5 sm:p-6" style={{ background: "#4169E1" }}>
                <div className="grid grid-cols-2 gap-4 sm:gap-5">
                  {(p.team_members || []).map((m, i) => (
                    <div key={m.name || `member-${i}`} className="text-center" data-testid={`proposal-team-member-${i}`}>
                      <div className="aspect-square rounded-full overflow-hidden border-4 mx-auto max-w-[10rem]" style={{ borderColor: "#7aa2ff", background: "#e5edff" }}>
                        {m.image_url ? (
                          <img src={m.image_url} alt={m.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white/60 text-xs uppercase">
                            No photo
                          </div>
                        )}
                      </div>
                      <div className="mt-2 text-white font-bold text-base sm:text-lg leading-tight">{m.name}</div>
                      <div className="text-white/90 text-xs sm:text-sm leading-tight">{m.role}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ============ SIGNAGE REFERENCE ============ */}
      {(p.signage_intro || p.signage_internal_image_url || p.signage_outdoor_image_url || tiers.length > 0) && (
        <section className="border-t border-border" data-testid="proposal-signage">
          <div className="mx-auto max-w-4xl px-5 sm:px-8 py-10 sm:py-14">
            <ProposalHeading kicker="Sponsor" title="Signage" />
            {p.signage_intro && (
              <p className="mt-4 text-sm sm:text-base whitespace-pre-line leading-relaxed max-w-2xl">{p.signage_intro}</p>
            )}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-10 items-start">
              {/* Internal signage */}
              <div>
                <div className="text-lg sm:text-xl font-bold mb-2">Internal Signage</div>
                <div className="rounded-2xl border border-border overflow-hidden bg-white flex items-center justify-center min-h-[260px] p-4">
                  {p.signage_internal_image_url ? (
                    <img src={p.signage_internal_image_url} alt="Internal signage" className="max-w-full max-h-[480px] object-contain" />
                  ) : (
                    <SignageInternalSVG tiers={tiers} />
                  )}
                </div>
                <div className="text-[11px] text-muted-foreground mt-2">850mm x 2000mm roll-up banner</div>
              </div>
              {/* Outdoor signage */}
              <div>
                <div className="text-lg sm:text-xl font-bold mb-2">Outdoor Signage</div>
                <div className="rounded-2xl border border-border overflow-hidden bg-white flex items-center justify-center min-h-[260px] p-4">
                  {p.signage_outdoor_image_url ? (
                    <img src={p.signage_outdoor_image_url} alt="Outdoor signage" className="max-w-full max-h-[480px] object-contain" />
                  ) : (
                    <SignageOutdoorSVG tiers={tiers} />
                  )}
                </div>
                <div className="text-[11px] text-muted-foreground mt-2">2000mm x 900mm double-sided</div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ CLUB SPONSORSHIP FORM ============ */}
      <SponsorshipFormSection p={p} tiers={tiers} />

      {/* ============ CTA FOOTER ============ */}
      <section className="border-t border-border bg-[hsl(var(--fg))] text-[hsl(var(--bg))]" data-testid="proposal-cta">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-10 text-center">
          <h3
            className="text-3xl sm:text-5xl uppercase tracking-tight leading-none"
            style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif", fontWeight: 900 }}
          >
            Ready to join the Derwent family?
          </h3>
          <p className="mt-3 text-sm sm:text-base opacity-80">Reach out and we'll tailor a package to your business.</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Button
              asChild
              className="rounded-full bg-white text-[hsl(var(--fg))] hover:bg-white/90 h-11 px-6"
              data-testid="proposal-email-btn"
            >
              <a href={`mailto:${p.team_email || "derwentcc@gmail.com"}`}>Email us</a>
            </Button>
            <Link to="/" data-testid="proposal-back-home">
              <Button variant="outline" className="rounded-full border-white/30 text-white hover:bg-white/10 h-11 px-6">
                Back to home
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function ProposalHeading({ kicker, title, small }) {
  return (
    <div className="leading-none">
      <div
        className="text-2xl sm:text-3xl italic text-[#4169E1] mb-1"
        style={{ fontFamily: "'Great Vibes', 'Barlow Condensed', cursive" }}
      >
        {kicker}
      </div>
      <div
        className={`${small ? "text-3xl sm:text-4xl" : "text-5xl sm:text-6xl"} uppercase tracking-tight`}
        style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif", fontWeight: 900, color: "#1e3a8a" }}
      >
        {title}
      </div>
    </div>
  );
}

function TierBlock({ tier, flip }) {
  const photo = (
    <div className="shrink-0 w-40 sm:w-64 aspect-square rounded-full overflow-hidden border-4" style={{ borderColor: "#4169E1" }}>
      {tier.image_url ? (
        <img src={tier.image_url} alt={`${tier.name} sponsor`} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-[hsl(var(--secondary))]" />
      )}
    </div>
  );
  const copy = (
    <div className="min-w-0 flex-1">
      <ProposalHeading kicker={tier.name} title="Sponsor" />
      <div className="mt-3 text-2xl sm:text-3xl font-semibold" style={{ color: "#4169E1" }}>{tier.price}</div>
      {tier.term && <div className="text-xs sm:text-sm font-bold tracking-[0.15em] uppercase mt-1">{tier.term}</div>}
      <div className="mt-4 font-bold">Key Benefits</div>
      <ul className="mt-2 space-y-1.5 text-sm sm:text-base list-disc pl-5">
        {(tier.bullets || []).map((b, i) => <li key={i} className="leading-relaxed whitespace-pre-line">{b}</li>)}
      </ul>
    </div>
  );
  return (
    <div className={`flex flex-col ${flip ? "sm:flex-row-reverse" : "sm:flex-row"} items-start gap-6 sm:gap-10`} data-testid={`proposal-tier-${(tier.name || "").toLowerCase()}`}>
      {photo}
      {copy}
    </div>
  );
}


/* -------------------------------------------------------------------------- *
 * Auto-generated signage diagrams — regenerate from the tier list so the
 * roll-up banner and outdoor fence reflect the sponsor packages the club
 * currently sells. Admins can override either with a real photo.
 * -------------------------------------------------------------------------- */
const SIGNAGE_ORDER = ["gold", "silver", "bronze", "round", "player", "mens player", "womans player", "junior player"];
function _tiersForSignage(tiers) {
  // Reduce tiers to categories present in the current proposal, preserving
  // rough priority order so signage always reads "biggest at top".
  const map = new Map();
  (tiers || []).forEach((t) => {
    const name = (t.name || "").toLowerCase();
    let cat = null;
    if (name.startsWith("gold")) cat = "Gold";
    else if (name.startsWith("silver")) cat = "Silver";
    else if (name.startsWith("bronze")) cat = "Bronze";
    else if (name.startsWith("round")) cat = "Round";
    else if (name.includes("player")) cat = "Player";
    if (cat && !map.has(cat)) map.set(cat, true);
  });
  return ["Gold", "Silver", "Bronze", "Round", "Player"].filter((c) => map.has(c));
}

function SignageInternalSVG({ tiers }) {
  const cats = _tiersForSignage(tiers);
  const rowHeightByCat = { Gold: 80, Silver: 74, Bronze: 60, Round: 44, Player: 96 };
  // Stack vertically at aspect ~0.4 (banner is tall and narrow).
  const w = 180;
  const gap = 6;
  const rows = cats.map((c) => ({ cat: c, h: rowHeightByCat[c] || 40 }));
  const totalH = rows.reduce((s, r) => s + r.h + gap, 0) + 20;
  let y = 12;
  return (
    <svg viewBox={`0 0 ${w} ${totalH}`} className="w-full max-w-[220px] h-auto" aria-label="Internal signage layout">
      <rect x="0" y="0" width={w} height={totalH} rx="8" fill="#f8fafc" stroke="#cbd5e1" />
      <rect x="0" y="0" width={w} height="8" fill="#94a3b8" rx="4" />
      {rows.map((r, i) => {
        const cy = y;
        y += r.h + gap;
        if (r.cat === "Round") {
          const cellW = (w - 16 - gap) / 2;
          return (
            <g key={i}>
              <rect x="8" y={cy} width={cellW} height={r.h} rx="4" fill="#ffffff" stroke="#334155" />
              <text x={8 + cellW / 2} y={cy + r.h / 2 + 4} textAnchor="middle" fontSize="10" fontWeight="700" fill="#0f172a">Round</text>
              <rect x={8 + cellW + gap} y={cy} width={cellW} height={r.h} rx="4" fill="#ffffff" stroke="#334155" />
              <text x={8 + cellW + gap + cellW / 2} y={cy + r.h / 2 + 4} textAnchor="middle" fontSize="10" fontWeight="700" fill="#0f172a">Round</text>
            </g>
          );
        }
        if (r.cat === "Player") {
          const cols = 4;
          const rowsInPlayer = 2;
          const cellW = (w - 16 - gap * (cols - 1)) / cols;
          const cellH = (r.h - gap) / rowsInPlayer;
          const elements = [];
          for (let rr = 0; rr < rowsInPlayer; rr++) {
            for (let cc = 0; cc < cols; cc++) {
              const x = 8 + cc * (cellW + gap);
              const yy = cy + rr * (cellH + gap);
              elements.push(<rect key={`${rr}-${cc}`} x={x} y={yy} width={cellW} height={cellH} rx="3" fill="#ffffff" stroke="#334155" />);
            }
          }
          return (
            <g key={i}>
              {elements}
              <text x={w / 2} y={cy + r.h + 2} textAnchor="middle" fontSize="9" fontWeight="700" fill="#0f172a">Player Sponsors</text>
            </g>
          );
        }
        return (
          <g key={i}>
            <rect x="8" y={cy} width={w - 16} height={r.h} rx="4" fill="#ffffff" stroke="#334155" />
            <text x={w / 2} y={cy + r.h / 2 + 4} textAnchor="middle" fontSize="12" fontWeight="800" fill="#0f172a">{r.cat}</text>
          </g>
        );
      })}
    </svg>
  );
}

function SignageOutdoorSVG({ tiers }) {
  const cats = _tiersForSignage(tiers);
  const w = 280;
  const rowH = 60;
  const gap = 12;
  const rows = [];
  if (cats.includes("Gold")) rows.push({ cat: "Gold" });
  if (cats.includes("Silver")) rows.push({ cat: "Silver" });
  if (cats.includes("Bronze")) rows.push({ cat: "Bronze", split: 2 });
  if (cats.includes("Round") || cats.includes("Player")) rows.push({ cat: "Round-Player" });
  const totalH = rows.length * (rowH + gap);
  return (
    <svg viewBox={`0 0 ${w} ${totalH}`} className="w-full max-w-[320px] h-auto" aria-label="Outdoor signage layout">
      {rows.map((r, i) => {
        const y = i * (rowH + gap);
        if (r.split === 2) {
          const cellW = (w - 12 - gap) / 2;
          return (
            <g key={i}>
              <FenceRail x={6} y={y} w={cellW} h={rowH} label="Bronze" />
              <FenceRail x={6 + cellW + gap} y={y} w={cellW} h={rowH} label="Bronze" />
            </g>
          );
        }
        if (r.cat === "Round-Player") {
          const cellW = (w - 12 - gap) / 2;
          return (
            <g key={i}>
              <FenceRail x={6} y={y} w={cellW} h={rowH} label="Round" small />
              <FenceRail x={6 + cellW + gap} y={y} w={cellW} h={rowH} label="Player Sponsors" small />
            </g>
          );
        }
        return <FenceRail key={i} x={6} y={y} w={w - 12} h={rowH} label={r.cat} />;
      })}
    </svg>
  );
}

function FenceRail({ x, y, w, h, label, small }) {
  const railColor = "#0f172a";
  return (
    <g>
      {/* Rails */}
      <rect x={x} y={y + 2} width={w} height={4} rx="2" fill={railColor} />
      <rect x={x} y={y + h - 8} width={w} height={4} rx="2" fill={railColor} />
      {/* Posts */}
      <rect x={x} y={y} width={4} height={h} rx="2" fill={railColor} />
      <rect x={x + w - 4} y={y} width={4} height={h} rx="2" fill={railColor} />
      {/* Signage panel */}
      <rect x={x + 6} y={y + 8} width={w - 12} height={h - 18} fill="#ffffff" stroke="#334155" rx="3" />
      <text x={x + w / 2} y={y + h / 2 + (small ? 2 : 4)} textAnchor="middle" fontSize={small ? "10" : "14"} fontWeight="800" fill="#0f172a">{label}</text>
    </g>
  );
}

/* -------------------------------------------------------------------------- *
 * Fillable sponsorship form. Renders a print-style layout with checkbox
 * grid + org fields, posts to /api/sponsorship-proposal/submit which
 * stores the enquiry and emails (mocked) the club's return-to address.
 * -------------------------------------------------------------------------- */
function SponsorshipFormSection({ p, tiers }) {
  // Build the checkbox list: every tier gets a checkbox + price, then extras.
  const tierOptions = useMemo(() => (tiers || []).map((t) => ({
    label: (t.name || "").toUpperCase() + (t.term ? ` (${t.term})` : ""),
    value: t.name,
    price: t.price || "",
  })), [tiers]);
  const extraOptions = (p.form_extra_options || []).map((label) => ({ label: label.toUpperCase(), value: label, price: "TBC" }));
  const allOptions = [...tierOptions, ...extraOptions];

  const [monetary, setMonetary] = useState(false);
  const [inKind, setInKind] = useState(false);
  const [inKindDetails, setInKindDetails] = useState("");
  const [selected, setSelected] = useState({});
  const [organisation, setOrganisation] = useState("");
  const [contactName, setContactName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const toggle = (v) => setSelected((prev) => ({ ...prev, [v]: !prev[v] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!organisation && !contactName && !email && !phone) {
      toast.error("Please add at least a name, organisation, phone or email so we can follow up");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/sponsorship-proposal/submit", {
        monetary,
        in_kind: inKind,
        in_kind_details: inKindDetails,
        selected_options: Object.keys(selected).filter((k) => selected[k]),
        organisation,
        contact_name: contactName,
        phone,
        email,
        message,
      });
      // Option B: open the visitor's default mail client with the enquiry
      // pre-filled so they hit "Send" and it leaves from their own account.
      // The submission has already been persisted server-side either way.
      if (data.mailto_href) {
        // window.open with _self to avoid popup blockers on mobile.
        window.open(data.mailto_href, "_self");
        toast.success(`Opening your email app to send this enquiry to ${data.return_email || "the club"}`);
      } else {
        toast.success(`Sent! We'll be in touch at ${email || phone || organisation}.`);
      }
      setMonetary(false); setInKind(false); setInKindDetails("");
      setSelected({}); setOrganisation(""); setContactName("");
      setPhone(""); setEmail(""); setMessage("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="border-t border-border bg-[hsl(var(--secondary))]/40" data-testid="proposal-form">
      <div className="mx-auto max-w-4xl px-5 sm:px-8 py-10 sm:py-14">
        {/* Hero photos */}
        {(p.form_hero_image_urls || []).filter(Boolean).length > 0 && (
          <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-6">
            {(p.form_hero_image_urls || []).filter(Boolean).slice(0, 3).map((u, i) => (
              <div key={u || `hero-${i}`} className="aspect-[4/3] rounded-xl overflow-hidden">
                <img src={u} alt={`Team photo ${i + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        )}
        <div className="text-center">
          <h2
            className="text-3xl sm:text-5xl uppercase tracking-tight leading-none"
            style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif", fontWeight: 900, color: "#1e3a8a" }}
          >
            Club Sponsorship Form
          </h2>
          {p.form_intro && (
            <p className="mt-3 text-sm sm:text-base text-foreground max-w-2xl mx-auto whitespace-pre-line">{p.form_intro}</p>
          )}
        </div>

        <form onSubmit={submit} className="mt-8 space-y-6 rounded-2xl bg-background border border-border p-5 sm:p-8" data-testid="proposal-form-fields">
          {/* Monetary / in-kind */}
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Checkbox id="monetary" checked={monetary} onCheckedChange={(v) => setMonetary(!!v)} data-testid="form-monetary-cb" />
              <Label htmlFor="monetary" className="text-sm sm:text-base leading-tight cursor-pointer">
                Yes! I would like to provide a monetary sponsorship to the club
              </Label>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox id="in-kind" checked={inKind} onCheckedChange={(v) => setInKind(!!v)} data-testid="form-inkind-cb" />
              <Label htmlFor="in-kind" className="text-sm sm:text-base leading-tight cursor-pointer">
                I can't provide a monetary sponsorship, but can provide in-kind support with vouchers or donations (please specify below)
              </Label>
            </div>
            {inKind && (
              <Textarea
                value={inKindDetails}
                onChange={(e) => setInKindDetails(e.target.value)}
                rows={3}
                placeholder="Describe the in-kind support you can provide"
                data-testid="form-inkind-details"
              />
            )}
          </div>

          {/* Sponsorship option checkboxes */}
          {allOptions.length > 0 && (
            <div>
              <div
                className="text-lg sm:text-xl font-bold uppercase tracking-tight mb-3"
                style={{ color: "#1e3a8a", fontFamily: "'Barlow Condensed', 'Inter', sans-serif" }}
              >
                Please check appropriate sponsorship(s):
              </div>
              <div className="grid sm:grid-cols-2 gap-2 sm:gap-3">
                {allOptions.map((o, i) => (
                  <label
                    key={i}
                    className="flex items-center gap-3 rounded-lg border border-border bg-background hover:bg-[hsl(var(--secondary))]/50 px-3 py-2 cursor-pointer"
                    data-testid={`form-option-${(o.value || "").toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Checkbox checked={!!selected[o.value]} onCheckedChange={() => toggle(o.value)} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs sm:text-sm font-semibold leading-tight">{o.label}</div>
                    </div>
                    <div className="text-sm sm:text-base font-bold shrink-0" style={{ color: "#4169E1" }}>{o.price}</div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Org info */}
          <div>
            <div
              className="text-lg sm:text-xl font-bold uppercase tracking-tight mb-3"
              style={{ color: "#1e3a8a", fontFamily: "'Barlow Condensed', 'Inter', sans-serif" }}
            >
              Organisation Information
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Organisation" value={organisation} onChange={setOrganisation} testid="form-org" />
              <Field label="Contact name" value={contactName} onChange={setContactName} testid="form-contact" />
              <Field label="Phone" value={phone} onChange={setPhone} testid="form-phone" />
              <Field label="Email" type="email" value={email} onChange={setEmail} testid="form-email" />
            </div>
            <div className="mt-3">
              <Label className="text-xs font-medium">Message (optional)</Label>
              <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="form-message" />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2 border-t border-border">
            <div className="text-xs sm:text-sm text-muted-foreground text-center sm:text-left">
              Send opens your email app pre-filled — press <span className="font-semibold text-foreground">Send</span> there to deliver to <span className="font-semibold text-foreground">{p.form_return_email || "derwentcc@gmail.com"}</span>
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]/90 h-11 px-6"
              data-testid="form-submit-btn"
            >
              <Send className="h-4 w-4 mr-2" /> {submitting ? "Opening…" : "Send enquiry"}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="text-xs font-medium">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}
